import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

// Types
import { UpdateAiSettingType, IntegrationSources } from 'Shared/types/UpdateAiSettingType.type';
import { UpdateAiTemplateType, UpdateAiTemplateSectionType } from 'Shared/types/UpdateAiTemplate.type';
import { UpdateAiReportType } from 'Shared/types/UpdateAiReportType.type';

// Selectors
import {
  getUpdateAiTempateBySlug,
  getUpdateAiSettingBySlug,
  getUpdateAiTemplateSections,
  getDefaultUpdateAiTemplatesByTemplateType,
  getDraftReportByUpdateAiSlug,
} from 'Shared/selectors/UpdateAiSettings.selectors';

// Actions
import {
  fetchUpdateAiSetting,
  updateUpdateAiSetting,
  createUpdateAiSettingTemplateSection,
} from 'Shared/actions/UpdateAiSettings.actions';

type Props = {
  slug: string;
  withFetch?: boolean;
};

export const useUpdateAiSetting = ({ slug, withFetch = false }: Props) => {
  const dispatch = useDispatch();

  const [isFetching, setIsFetching] = useState(false);

  const updateAiSetting: UpdateAiSettingType = useSelector(state => getUpdateAiSettingBySlug(state, { slug }));
  const template: UpdateAiTemplateType = useSelector(state =>
    getUpdateAiTempateBySlug(state, { templateSlug: updateAiSetting?.updateAiTemplateSlug })
  );
  const sections: UpdateAiTemplateSectionType[] = useSelector(state =>
    getUpdateAiTemplateSections(state, { templateSectionSlugs: updateAiSetting?.templateSectionSlugs })
  );
  const defaultTemplate: UpdateAiTemplateType = useSelector(state =>
    getDefaultUpdateAiTemplatesByTemplateType(state, { templateType: updateAiSetting?.templateType })
  );

  const report: UpdateAiReportType = useSelector(state =>
    getDraftReportByUpdateAiSlug(state, { slug, draft: true })
  )?.toJS();

  const isLoaded = Boolean(updateAiSetting);
  const hasGeneratedSections = report?.body?.length > 0;

  const slackSourceConfiguration =
    (isLoaded &&
      updateAiSetting.dataSources.find(ds => ds.dataSourceSlug === IntegrationSources.SLACK_AI)?.configuration) ||
    null;
  const hasSlackConfigured = slackSourceConfiguration && slackSourceConfiguration.length > 0;

  const jiraSourceConfiguration =
    (isLoaded &&
      updateAiSetting.dataSources.find(ds => ds.dataSourceSlug === IntegrationSources.JIRA)?.configuration) ||
    null;
  const hasJiraConfigured = jiraSourceConfiguration && !isEmpty(jiraSourceConfiguration);

  const asanaSourceConfiguration =
    (isLoaded &&
      updateAiSetting.dataSources.find(ds => ds.dataSourceSlug === IntegrationSources.ASANA)?.configuration) ||
    null;
  const hasAsanaConfigured = asanaSourceConfiguration && !isEmpty(asanaSourceConfiguration);

  const hubspotSourceConfiguration =
    (isLoaded &&
      updateAiSetting.dataSources.find(ds => ds.dataSourceSlug === IntegrationSources.HUBSPOT)?.configuration) ||
    null;
  const hasHubspotConfigured = Boolean(hubspotSourceConfiguration);

  const salesforceSourceConfiguration =
    (isLoaded &&
      updateAiSetting.dataSources.find(ds => ds.dataSourceSlug === IntegrationSources.SALEFORCE)?.configuration) ||
    null;
  const hasSalesforceConfigured = Boolean(salesforceSourceConfiguration);

  const handleUpdateAiSetting = ({ dataSources }: { dataSources? }) => {
    dispatch(updateUpdateAiSetting({ slug, dataSources }));
  };

  const handleAddSection = ({
    title = '',
    description = '',
  }: Partial<{ title?: string; description?: string }> = {}) => {
    return dispatch(
      createUpdateAiSettingTemplateSection({
        slug,
        data: {
          title: title,
          description: description,
        },
      })
    ).then(response => response);
  };

  useEffect(() => {
    if (slug && withFetch) {
      setIsFetching(true);
      dispatch(fetchUpdateAiSetting({ slug })).finally(() => {
        setIsFetching(false);
      });
    }
  }, [slug]);

  return {
    isFetching,
    isLoaded,
    updateAiSetting,
    template,
    sections,
    defaultTemplate,
    hasGeneratedSections,
    report,
    handleUpdateAiSetting,
    handleAddSection,
    slackSourceConfiguration,
    hasSlackConfigured,
    jiraSourceConfiguration,
    hasJiraConfigured,
    asanaSourceConfiguration,
    hasAsanaConfigured,
    hubspotSourceConfiguration,
    hasHubspotConfigured,
    salesforceSourceConfiguration,
    hasSalesforceConfigured,
  };
};
